import { TitleCasePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';

import { ITimesheet, ITimesheetEntry, IRuleViolation } from '@core/interfaces';

import { TimesheetFormService } from '@shared/service/timesheet-form.service';
import {
  generateWeekDays,
  getEntryForDate,
  calculateTotalHours,
} from '@shared/utils/timesheet.utils';

import { TimesheetRowCellComponent } from './timesheet-row-cell.component';

export interface HoursChangedEvent {
  timesheetId: number;
  date: string;
  hours: number;
}

@Component({
  selector: 'app-timesheet-table',
  templateUrl: './timesheet-table.component.html',
  imports: [
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TitleCasePipe,
    TimesheetRowCellComponent,
  ],
})
export class TimesheetTableComponent {
  protected formService = inject(TimesheetFormService);

  // Inputs
  timesheets = input.required<ITimesheet[]>();
  weekStart = input.required<string | DateTime>();
  violations = input<IRuleViolation[]>([]);
  showEditActions = input<boolean>(false);
  showKekaMetadata = input<boolean>(false);

  // Outputs
  hoursChanged = output<HoursChangedEvent>();
  editTimesheet = output<ITimesheet>();
  saveTimesheet = output<ITimesheet>();
  cancelEdit = output<number>();

  // Computed
  weekDays = computed(() => {
    const ws = this.weekStart();
    const weekStartStr = DateTime.isDateTime(ws) ? ws.toISODate()! : ws;
    return generateWeekDays(weekStartStr);
  });

  displayedColumns = computed(() => {
    const cols = ['source', ...this.weekDays().map((d) => d.date), 'total'];
    if (this.showEditActions()) {
      cols.push('actions');
    }
    return cols;
  });

  trustedTimesheet = computed(() => this.timesheets().find((ts) => ts.source.is_trusted));

  violationsByDate = computed(() => {
    const map = new Map<string, IRuleViolation[]>();
    for (const violation of this.violations()) {
      const date = violation.details?.['date'] as string | undefined;
      if (date) {
        const existing = map.get(date) || [];
        map.set(date, [...existing, violation]);
      }
    }
    return map;
  });

  // Methods

  /**
   * Get the key to use for a timesheet (id if available, otherwise source_id).
   */
  private getTimesheetKey(timesheet: ITimesheet): number {
    return timesheet.id || timesheet.source_id;
  }

  getEntry(timesheet: ITimesheet, day: string): ITimesheetEntry | undefined {
    // If editing, get from form service
    const key = this.getTimesheetKey(timesheet);
    if (key && this.formService.isEditing(key)) {
      const editing = this.formService.getEditingTimesheet(key);
      if (editing) {
        return getEntryForDate(editing.entries, day);
      }
    }
    return getEntryForDate(timesheet.entries, day);
  }

  getTotal(timesheet: ITimesheet): number {
    const key = this.getTimesheetKey(timesheet);
    if (key && this.formService.isEditing(key)) {
      const editing = this.formService.getEditingTimesheet(key);
      if (editing) {
        return calculateTotalHours(editing.entries);
      }
    }
    return calculateTotalHours(timesheet.entries);
  }

  hasError(timesheet: ITimesheet, date: string): boolean {
    const violations = this.violationsByDate().get(date);
    if (!violations) return false;

    return violations.some((v) => {
      if (v.severity !== 'Error') return false;
      const sourceId = v.details?.['source_id'] as number | undefined;
      return !sourceId || sourceId === timesheet.source_id;
    });
  }

  isWithinTrusted(timesheet: ITimesheet, day: string): boolean {
    if (timesheet.source.is_trusted) return false;

    const trusted = this.trustedTimesheet();
    if (!trusted) return false;

    const tsEntry = getEntryForDate(timesheet.entries, day);
    const trustedEntry = getEntryForDate(trusted.entries, day);

    if (!tsEntry || !trustedEntry) return false;

    return tsEntry.submitted_hours <= trustedEntry.submitted_hours;
  }

  isEditing(timesheet: ITimesheet): boolean {
    const key = this.getTimesheetKey(timesheet);
    return !!key && this.formService.isEditing(key);
  }

  onHoursChanged(timesheetId: number, date: string, hours: number): void {
    this.formService.updateEntry(timesheetId, date, hours);
    this.hoursChanged.emit({ timesheetId, date, hours });
  }

  onEditClick(timesheet: ITimesheet): void {
    this.formService.startEditing(timesheet);
    this.editTimesheet.emit(timesheet);
  }

  onSaveClick(timesheet: ITimesheet): void {
    const key = this.getTimesheetKey(timesheet);
    if (!key) return;
    const updated = this.formService.commitEditing(key);
    if (updated) {
      this.saveTimesheet.emit(updated);
    }
  }

  onCancelClick(timesheet: ITimesheet): void {
    const key = this.getTimesheetKey(timesheet);
    if (!key) return;
    this.formService.cancelEditing(key);
    this.cancelEdit.emit(key);
  }
}
